import { Children, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { FieldInputProps, FormikValues, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import ReactSelect, { MenuPlacement, components } from 'react-select';
import cn from 'classnames';
import { getDataTest, isPoland } from '../../utils';
import './Select.styles.css';

export interface SelectOptionProps {
    label: string;
    value: string | number;
}

export interface SportLevelSelectProps {
    code?: string;
    value: string | number;
}

export interface SelectProps {
    options: Array<SelectOptionProps>;
    id: string;
    name: string;
    field?: FieldInputProps<string | number>;
    form?: FormikValues;
    value?: string | number;
    setFieldValue?: (field: string, value: string | number, shouldValidate?: boolean) => void;
    className?: string;
    level?: SportLevelSelectProps;
    placeholder?: string;
    isSearchable?: boolean;
    disabled?: boolean;
    maxWidth?: string;
    isCenter?: boolean;
    isMulti?: boolean;
}

export const Select = ({
    options,
    className,
    form,
    setFieldValue,
    level,
    isSearchable = true,
    disabled = false,
    maxWidth,
    isCenter = false,
    isMulti = false,
    ...props
}: SelectProps): ReactElement | null => {
    const { t } = useTranslation();

    const vcChildRef = useRef<any>();
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [menuPlacement, setMenuPlacement] = useState<MenuPlacement | undefined>('auto');
    const [menuHeight, setMenuHeight] = useState(300);

    const [field, meta] = useField(props);
    const { error, touched } = meta;

    const placeholder = props.placeholder || t('general.chooseValue');

    const Control = ({ children, ...controlProps }: any) => (
        <components.Control {...controlProps}>
            <div className="relative flex w-full">{children}</div>
        </components.Control>
    );

    const IndicatorsContainer = ({ children, ...continerProps }: any) => (
        <components.IndicatorsContainer {...continerProps}>
            <div className="flex flex-row" data-test={getDataTest(props, 'Indicators')}>
                {children}
            </div>
        </components.IndicatorsContainer>
    );

    const DropdownIndicator = ({ children, ...dropdownProps }: any) =>
        disabled ? null : (
            <components.DropdownIndicator
                {...dropdownProps}
                className={cn(
                    'flex h-6 w-6 rotate-0 items-center justify-center rounded-full bg-purple !p-1 pl-4 !text-white transition-all',
                    dropdownProps.selectProps.menuIsOpen && '!rotate-180',
                    disabled && '!bg-gray'
                )}
            >
                {children}
            </components.DropdownIndicator>
        );

    const MenuList = ({ children, ...menuProps }: any) => (
        <components.MenuList {...menuProps} className="scrollbar z-10 my-3" maxHeight={menuHeight}>
            <div data-test={getDataTest(props, 'MenuList')}>{children}</div>
        </components.MenuList>
    );

    const Option = ({ children, ...optionProps }: any) => (
        <components.Option {...optionProps}>
            <div data-test={getDataTest(props, 'Option')}>{children}</div>
        </components.Option>
    );

    const CheckBoxOption = ({ Icon, children, isSelected, ...rest }: any) => {
        return (
            <components.Option {...rest}>
                <div
                    data-test={getDataTest(props, 'Option')}
                    className="text-nowrap w-90 overflow-hidden text-ellipsis whitespace-nowrap"
                >
                    <input type="checkbox" defaultChecked={isSelected} className="mr-2" />
                    {children}
                </div>
            </components.Option>
        );
    };

    const SelectContainer = ({ children, ...containerProps }: any) => (
        <components.SelectContainer {...containerProps}>
            <div data-test={getDataTest(props, 'Input')}>{children}</div>
        </components.SelectContainer>
    );

    const ValueContainer = ({ children, getValue, ...rest }: any) => {
        if (!isMulti) {
            return <components.ValueContainer {...rest}>{children}</components.ValueContainer>;
        }

        var length = getValue().length;
        let displayChips =
            length > 1 ? (
                <span>
                    {t('common.selectedOptionsMessage')} ({length})
                </span>
            ) : (
                Children.toArray(children)[0]
            );

        return (
            <components.ValueContainer {...rest} style={{ padding: 0 }}>
                <div
                    className="text-nowrap w-90 w-full overflow-hidden text-ellipsis whitespace-nowrap"
                    onClick={() => {
                        vcChildRef.current.onInputFocus();
                    }}
                >
                    {displayChips}
                </div>
            </components.ValueContainer>
        );
    };

    const MultiValueContainer = ({ data }: any) => {
        if (data) {
            return data.label;
        }
        return '';
    };

    const onScroll = useCallback(() => {
        if (menuRef?.current) {
            if (menuRef?.current?.getBoundingClientRect().top > window.innerHeight / 2) {
                setMenuPlacement('top');
            } else {
                setMenuPlacement('bottom');
            }
            if (window.innerHeight < 800) {
                setMenuHeight(180);
            } else if (menuHeight !== 300) {
                setMenuHeight(300);
            }
        }
    }, [menuRef, menuHeight]);

    useEffect(() => {
        window.addEventListener('scroll', onScroll, true);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [onScroll]);

    useEffect(() => {
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('resize', onScroll);
        };
    }, [onScroll]);

    useEffect(() => {
        onScroll();
    }, [onScroll]);

    return (
        <div
            style={{
                maxWidth,
                margin: '0 auto',
                width: '100%',
                ...(isCenter && { textAlign: 'center' }),
            }}
            data-test={getDataTest(props)}
            ref={menuRef}
        >
            <ReactSelect
                {...props}
                ref={vcChildRef}
                id={field.name}
                key={field.name}
                name={field.name}
                className={cn(`react-select ${!!error && touched ? 'hasError' : ''}`, isCenter && 'text-center-force')}
                classNamePrefix={cn(isPoland() && 'react-select-pl', 'react-select')}
                options={options}
                placeholder={placeholder}
                menuPortalTarget={document.body}
                isSearchable={isSearchable}
                isDisabled={disabled}
                menuPosition="fixed"
                menuPlacement={menuPlacement}
                menuShouldBlockScroll
                menuShouldScrollIntoView
                // TODO: resolve typescript error
                // @ts-ignore
                onChange={(option: SelectOptionProps) => {
                    setFieldValue
                        ? setFieldValue(field.name || '', option.value)
                        : form?.setFieldValue(field.name, option.value);
                }}
                value={
                    level
                        ? options.find((o: SelectOptionProps) => o.value === level.value)
                        : options
                          ? options.find((option: SelectOptionProps) => option.value === field?.value)
                          : ''
                }
                components={{
                    Control,
                    SelectContainer,
                    IndicatorSeparator: () => null,
                    IndicatorsContainer,
                    DropdownIndicator,
                    MenuList,
                    Option: isMulti ? CheckBoxOption : Option,
                    ValueContainer,
                    MultiValueContainer,
                }}
                data-test={getDataTest(props, 'Input')}
            />
        </div>
    );
};
